import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  useAssignAreaList,
  useCompanyList,
  type CompanyType,
} from "~/services/customer/home";
import { IconSearch } from "@/Icon/Search";
import { BottomArrow } from "@/Icon/BottomArrow";

interface SearchParams {
  region?: string;
  category?: string;
}

export const Route = createFileRoute("/customer/layout/map-list")({
  component: MapListPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    region: search.region as string | undefined,
    category: search.category as string | undefined,
  }),
});

type Category = { label: string; value: string | null; color: string };
const CATEGORIES: Category[] = [
  { label: "전체", value: null, color: "#999999" },
  { label: "미용실", value: "PROVIDED_GROOMING", color: "#F24747" },
  { label: "유치원", value: "PROVIDED_KINDERGARTEN", color: "#FF9934" },
  { label: "데이케어", value: "PROVIDED_DAYCARE", color: "#3DCB7F" },
  { label: "호텔", value: "PROVIDED_HOTEL", color: "#7D6DFF" },
];

const CATEGORY_LABEL: Record<string, string> = {
  PROVIDED_GROOMING: "미용실",
  PROVIDED_KINDERGARTEN: "유치원",
  PROVIDED_DAYCARE: "데이케어",
  PROVIDED_HOTEL: "호텔",
};

function MapListPage() {
  const { history } = useRouter();
  const { region: initialRegion, category: initialCategory } = Route.useSearch();
  const [category, setCategory] = useState<string | null>(
    initialCategory ?? null
  );
  const [keyword, setKeyword] = useState("");
  const [areaOpen, setAreaOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string>(
    initialRegion ?? ""
  );
  const [selectedRegionLabel, setSelectedRegionLabel] = useState<string>(
    initialRegion ?? "지역 선택"
  );

  const { data: assignAreaList } = useAssignAreaList();
  const { data: companyList = [] } = useCompanyList(
    { region: selectedRegion, category },
    !!selectedRegion
  );

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return companyList;
    return companyList.filter((c: CompanyType) =>
      c.companyName.toLowerCase().includes(q)
    );
  }, [companyList, keyword]);

  return (
    <div className="flex-auto bg-[#F5F5F5]">
      <header className="sticky top-0 z-10 flex h-14 items-center bg-white px-4">
        <button
          type="button"
          onClick={() => history.push("/customer/layout/map")}
          aria-label="뒤로가기"
          className="mr-2"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 6l-6 6 6 6"
              stroke="#111"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className="flex-1 text-center text-base font-bold">매장 목록</h1>
        <span className="w-6" />
      </header>

      <div className="bg-white px-4 pb-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setAreaOpen((v) => !v)}
            className="flex h-10 shrink-0 items-center gap-1 rounded-full border border-[#E5E5E5] bg-white px-4 text-sm font-bold"
          >
            {selectedRegionLabel}
            <BottomArrow />
          </button>
          <div className="relative flex-1">
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="매장 이름으로 검색"
              className="h-10 w-full rounded-full bg-[#F5F5F5] px-4 pr-10 text-sm outline-none placeholder:text-gray"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              <IconSearch />
            </span>
          </div>
        </div>

        {areaOpen && (
          <div className="mt-2 rounded-xl border border-[#E5E5E5] bg-white p-2 max-h-52 overflow-y-auto">
            {assignAreaList && assignAreaList.length > 0 ? (
              assignAreaList.map(
                (area: {
                  regionSeq: number;
                  regionName: string;
                  region: string;
                }) => (
                  <button
                    key={area.regionSeq}
                    type="button"
                    onClick={() => {
                      setSelectedRegion(area.region);
                      setSelectedRegionLabel(area.regionName || area.region);
                      setAreaOpen(false);
                    }}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-[#F5F5F5]"
                  >
                    {area.regionName || area.region}
                  </button>
                )
              )
            ) : (
              <div className="p-3 text-center text-xs text-gray">
                저장된 지역이 없습니다.
              </div>
            )}
          </div>
        )}

        <div className="-mx-4 mt-3 overflow-x-auto">
          <div className="flex gap-2 px-4">
            {CATEGORIES.map((cat) => {
              const active = category === cat.value;
              return (
                <button
                  key={cat.label}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`flex h-10 shrink-0 items-center gap-1 rounded-full border-2 px-4 text-sm font-bold bg-white ${
                    active
                      ? "border-yellow text-yellow"
                      : "border-[#E5E5E5] text-black"
                  }`}
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: cat.color }}
                  />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="px-4 py-3 text-sm font-bold">{filtered.length} 개</div>

      <div className="flex flex-col gap-2 px-4 pb-8">
        {filtered.length === 0 ? (
          <div className="rounded-xl bg-white p-6 text-center text-sm text-gray">
            표시할 매장이 없습니다.
          </div>
        ) : (
          filtered.map((c: CompanyType) => (
            <button
              key={c.companySeq}
              type="button"
              onClick={() =>
                history.push(
                  `/customer/layout/home-serach-detail?companySeq=${c.companySeq}`
                )
              }
              className="flex flex-col gap-2 rounded-xl bg-white p-4 text-left"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-base font-bold">
                    {c.companyName}
                  </div>
                  <div className="truncate text-xs text-gray">
                    {c.companyAddr}
                  </div>
                </div>
              </div>
              {c.provides && c.provides.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {c.provides
                    .filter((p) => p.available)
                    .map((p) => (
                      <span
                        key={p.name}
                        className="rounded-md bg-[#F5F5F5] px-2 py-0.5 text-[11px] text-gray"
                      >
                        {CATEGORY_LABEL[p.name] ?? p.name}
                      </span>
                    ))}
                </div>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
